import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { DEFAULT_BRANDING } from '../auth/brandingClient';
import { applyFontTokens, fontTokensForBranding } from '../tokens/fonts';

const BRANDING_CACHE_KEY = 'msp-branding-cache-v1';
const BRANDING_CACHE_TTL = 8 * 60 * 60 * 1000; // 8 hours

const BrandingContext = createContext(DEFAULT_BRANDING);
const BrandingUpdateContext = createContext(() => {});

function applyCssVariables(branding) {
  if (!branding) return;
  applyFontTokens(fontTokensForBranding(branding.fontFamily));
  const root = document.documentElement;
  if (branding.primaryColor) root.style.setProperty('--color-primary', branding.primaryColor);
  if (branding.secondaryColor) root.style.setProperty('--color-secondary', branding.secondaryColor);
  if (branding.backgroundColor) root.style.setProperty('--color-background', branding.backgroundColor);
  if (branding.textColor) root.style.setProperty('--color-text', branding.textColor);
  if (branding.surfaceColor) root.style.setProperty('--color-surface', branding.surfaceColor);
}

function setFavicon(logoUrl, logoLetter, primaryColor) {
  const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
  link.rel = 'icon';
  if (logoUrl) {
    link.href = logoUrl;
  } else {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = primaryColor || '#0d1f35';
    ctx.fillRect(0, 0, 32, 32);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(logoLetter || 'S', 16, 16);
    link.href = canvas.toDataURL();
  }
  document.head.appendChild(link);
}

function writeBrandingCache(data) {
  try {
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch (_) {}
}

function readBrandingCache() {
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > BRANDING_CACHE_TTL) return null;
    return data;
  } catch (_) {
    return null;
  }
}

export function clearBrandingCache() {
  try { localStorage.removeItem(BRANDING_CACHE_KEY); } catch (_) {}
}

export function BrandingProvider({ branding: initialBranding, children }) {
  // On first render: prefer the bootstrap branding when it was freshly fetched for this route.
  const [branding, setBranding] = useState(() => {
    if (initialBranding) return { ...DEFAULT_BRANDING, ...initialBranding };
    const cached = readBrandingCache();
    if (cached) return { ...DEFAULT_BRANDING, ...cached };
    return DEFAULT_BRANDING;
  });
  // Apply CSS variables whenever branding changes
  useEffect(() => {
    applyCssVariables(branding);
  }, [branding]);

  // Apply CSS variables and favicon immediately on mount from initial state
  useEffect(() => {
    applyCssVariables(branding);
    setFavicon(branding.logoUrl, branding.logoLetter, branding.primaryColor);
  }, []);

  // Direct update — apply branding without refetch; only cache if NOT impersonating
  const updateBranding = useCallback((data) => {
    if (!data) return;
    const merged = { ...DEFAULT_BRANDING, ...data };
    setBranding(merged);
    applyCssVariables(merged);
    writeBrandingCache(merged);
    document.title = (merged.portalName || 'Security Portal') + ' | Security Portal';
    setFavicon(merged.logoUrl, merged.logoLetter, merged.primaryColor);
  }, []);

  return (
    <BrandingContext.Provider value={branding}>
      <BrandingUpdateContext.Provider value={updateBranding}>
        {children}
      </BrandingUpdateContext.Provider>
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

export function useUpdateBranding() {
  return useContext(BrandingUpdateContext);
}
